<template>
  <app-page class="trip-page" fill lean>
    <!-- Aufbau wie die übrigen Tabellenseiten (Kundenwunsch 13.10.2026: „jetzt bitte alle shop seiten überarbeiten“):
         leichter Titel, Aktionen mit Beschriftung (Handy: Icon-Knöpfe); links Monat und Suche, rechts der Filter statt
         der Filterzeile (Datum wie bisher auf heute vorbelegt). Tabelle mit wenigen Bildschirmspalten wie Verkäufe, ein
         Klick auf eine Fahrt öffnet rechts den Detailbereich zum Bearbeiten (Kundenwunsch 13.10.2026) -->
    <template #actions>
      <template v-if="!handy">
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="download" @click="onExporting">Excel-Export</app-button>
      </template>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="download" label="Excel-Export" @click="onExporting" />
      </template>
    </template>
    <template #toolbar>
      <app-month-stepper v-model="month" />
      <app-search v-model="searchText" placeholder="In Fahrten suchen" @input="onSearchInput" />
    </template>
    <template #toolbar-end>
      <sale-filter-menu
        :value="filters"
        :facets="facets"
        :result-count="resultCount"
        :description="filterTooltip"
        :segment-defs="filterSegments"
        :active-filters="activeFilterCount"
        :result-nouns="resultNouns"
        :empty-value="emptyTripFilters"
        :show-range="false"
        :align-right="!handy"
        @open="refreshFacets"
        @input="onFiltersInput"
      />
    </template>

    <!-- Dialoge und Meldungen liegen über der Seite und nehmen keinen Platz im Inhalt ein -->
    <!-- Uhrzeiten als Liste mit Feldern statt Tabelle mit Filterzeile (Kundenwunsch 13.10.2026, alte Stellen prüfen) -->
    <shop-timetable-dialog v-model="dialog" :trip="selectedTrip" />

    <app-dialog v-model="emailDialog" :max-width="600" nested blur>
      <template #title>Nachricht an die Fahrgäste der Fahrt {{ selectedTripId }}</template>
      <div class="app-form-field">
        <label class="app-label" for="trip-notification-text">Nachricht</label>
        <app-text-field
          id="trip-notification-text"
          v-model="notificationText"
          textarea
          rows="5"
        />
      </div>
      <template #actions>
        <app-button @click="emailDialog = false">Abbrechen</app-button>
        <app-button
          kind="primary"
          icon="send"
          :loading="notificationSending"
          @click="sendNotification()"
        >Senden</app-button>
      </template>
    </app-dialog>

    <!-- Toasts unten rechts wie bei PayPal (Kundenwunsch 13.10.2026) -->
    <app-toast v-model="snackbarSuccess" icon="check" kind="ok" text="Benachrichtigung erfolgreich verschickt." closable />
    <app-toast
      v-model="snackbarError"
      icon="alert"
      kind="error"
      text="Benachrichtigung konnte nicht verschickt werden."
      closable
    />
    <app-toast v-model="savedSnackbar" :timeout="4000" icon="check" kind="ok" text="Fahrt gespeichert." />

    <app-dialog :value="discardDialog" title="Änderungen verwerfen?" @input="onDiscardDialogInput">
      <p class="trip-dialog__text">Die ungespeicherten Änderungen an der Fahrt gehen verloren.</p>
      <template #actions>
        <app-button @click="keepEditing">Weiter bearbeiten</app-button>
        <app-button kind="danger" @click="confirmDiscard">Verwerfen</app-button>
      </template>
    </app-dialog>

    <div class="trip-gridcol">
      <!-- Tabelle füllt die Höhe über der Fußleiste (Handy: natürliche Höhe) -->
      <div class="app-grid-fill trip-grid" :class="{ 'trip-grid--auto': handy }">
        <dx-data-grid
          :height="gridHeight"
          :columns="columns"
          :data-source="tripDataSource"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :column-auto-width="false"
          :allow-column-reordering="!gridTouch"
          :allow-column-resizing="!gridTouch"
          :hover-state-enabled="!gridTouch"
          :scrolling="scrolling"
          :element-attr="gridAttr"
          :ref="gridRefName"
          :focused-row-enabled="true"
          @toolbar-preparing="onToolbarPreparing"
          @focused-row-changing="onFocusedRowChanging"
          @focused-row-changed="onFocusedRowChanged"
          @content-ready="onContentReady"
        >

          <!-- Seitenwahl und Blättern in der Fußleiste (AppGridFooter) -->
          <dx-pager :visible="false" />
          <dx-paging :page-size="defaultPageSize" />

          <!-- Summen bleiben für den Excel-Export; auf dem Bildschirm stehen sie in der Fußleiste (Summenzeile ausgeblendet) -->
          <DxSummary :calculate-custom-summary="calculateSelectedRow">
            <DxTotalItem
              name="SelectedRowsSummary"
              summary-type="custom"
              display-format="Auslastung: {0}%"
              value-format="#######0.00"
              show-in-column="capacity"
            />
            <DxTotalItem
              column="price"
              summary-type="sum"
              display-format="Gesamt: {0}"
              value-format="#######0.00 €"
            />
          </DxSummary>

          <!-- Auslastung als Anteilsbalken wie im Dashboard (AppShareBar), farbig wie früher nach Belegung: unter 50 % grün,
               ab 50 % gelb, ab 80 % rot (Kundenwunsch 13.10.2026); daneben „belegt/gesamt“ -->
          <template #tripCapacityTemplate="cell">
            <div class="trip-capacity">
              <app-share-bar
                class="trip-capacity__bar"
                :parts="[{ value: Number(cell.data.data.capacity_used) || 0, color: capacityColor(cell.data.data) }]"
                :total="Number(cell.data.data.capacity) || 0"
              />
              <span class="trip-capacity__text">{{ cell.data.data.capacity_used }}/{{ cell.data.data.capacity }}</span>
            </div>
          </template>

          <!-- Uhrzeit, Datum und Strecke einzeilig (Strecke kürzt mit Ellipse) -->
          <template #timeCellTemplate="cell">
            <span class="trip-time">{{ cell.data.data.start || "–" }}</span>
          </template>
          <template #dayCellTemplate="cell">
            <span class="trip-day">{{ dayText(cell.data.data) }}</span>
          </template>
          <template #routeCellTemplate="cell">
            <span class="trip-route" :title="routeText(cell.data.data)">{{ routeText(cell.data.data) }}</span>
          </template>
          <template #priceCellTemplate="cell">
            <span class="trip-price">{{ euroText(cell.data.data.price) }}</span>
          </template>

          <!-- Zusatzplätze: nur belegte als Chips „Kindersitz 1/3“, sonst „frei“ -->
          <template #extrasCellTemplate="cell">
            <div class="trip-extras">
              <span v-for="extra in usedExtras(cell.data.data)" :key="extra.field" class="trip-extras__chip">
                {{ extra.name }} <strong>{{ extra.used }}/{{ extra.capacity }}</strong>
              </span>
              <span v-if="!usedExtras(cell.data.data).length" class="trip-extras__none">{{ extraTypes.length ? "frei" : "–" }}</span>
            </div>
          </template>

          <!-- Status als 7-px-Punkt mit Text -->
          <template #tripStatusTemplate="cell">
            <span class="trip-status">
              <span
                class="trip-status__dot"
                :class="cell.data.data.state == 'ACTIVE' ? 'trip-status__dot--ok' : 'trip-status__dot--off'"
                aria-hidden="true"
              ></span>
              {{ cell.data.text || cell.data.data.state }}
            </span>
          </template>
        </dx-data-grid>
      </div>
      <app-grid-footer ref="fuss" :page-sizes="pageSizes">
        <template #summary>
          <span>Umsatz <strong>{{ sumText("price") }}</strong></span>
          <span class="app-sep" aria-hidden="true">·</span>
          <span>Auslastung <strong>{{ utilizationText }}</strong></span>
          <span class="app-sep" aria-hidden="true">·</span>
          <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
          <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
        </template>
      </app-grid-footer>
    </div>

    <template #detail>
      <app-detail-pane
        :open="!!selectedTrip"
        wide
        icon="bus"
        :title="selectedTrip ? 'Fahrt ' + selectedTrip.trip_id : 'Fahrt'"
        close-label="Fahrt schließen"
        :dirty="dirty"
        @close="onDetailClose"
        @layout-change="onDetailLayout"
      >
        <template #subtitle>
          <span v-if="selectedTrip && selectedTrip.state === 'CANCELED'" class="trip-detail-tag">Storniert</span>
        </template>
        <shop-trip-detail
          v-if="selectedTrip"
          ref="tripDetail"
          :trip="selectedTrip"
          :value="draft"
          :errors="errors"
          :extra-types="extraTypes"
          @field="onField"
          @edit-times="editTimes"
          @notify="openNotification"
        />
        <!-- Knöpfe in einer Zeile, darüber nur bei Bedarf „n Änderungen“ bzw. ein Fehler (wie Benutzer) -->
        <template #footer>
          <span class="app-sr-only" role="status">{{ saveError || changeText }}</span>
          <span v-if="saveError || dirty" class="trip-foot__text" :class="{ 'trip-foot__text--error': !!saveError }" aria-hidden="true">
            <app-icon v-if="saveError" name="alert" :size="14" />{{ saveError || changeText }}
          </span>
          <app-button :disabled="!dirty || saving" @click="discardDraft">Verwerfen</app-button>
          <app-button kind="primary" icon="check" :disabled="!dirty || hasErrors" :loading="saving" @click="saveTrip">
            Speichern
          </app-button>
        </template>
      </app-detail-pane>
    </template>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxPager,
  DxSummary,
  DxTotalItem,
} from "devextreme-vue/data-grid";
import DataGridMixin from "@/mixins/DataGridMixin";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import ShopTimetableDialog from "@/components/shop/ShopTimetableDialog";
import ShopTripDetail from "@/components/shop/ShopTripDetail";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import moment from "moment";
import { formatEuro, formatNumber, plural } from "@/utils/format";
import query from "devextreme/data/query";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import { emptyFilters, activeCount, buildPredicate, buildFacets, describeFilters, NO_VALUE_TEXT } from "@/components/sales/saleFilters";
import { TRIP_FILTER_CONFIG } from "@/components/shop/shopFilters";

// Vorbelegung des Filters: Fahrten von heute (wie bisher filterValue der Spalte Datum)
function defaultTripFilters() {
  return Object.assign(emptyFilters(TRIP_FILTER_CONFIG), { days: [moment().format("YYYY-MM-DD")] });
}

export default {
  components: {
    DxDataGrid,
    DxPager,
    DxPaging,
    DxSummary,
    DxTotalItem,
    ShopTimetableDialog,
    ShopTripDetail,
    SaleFilterMenu,
  },
  mixins: [DataGridMixin],
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    // Fußleiste: Anzahl nach Filter und Suche, „4 von 24 …“ solange eingeschränkt
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Fahrt", "Fahrten");
    },
    summaryNoun() {
      return plural(this.summary.count, "Fahrt", "Fahrten");
    },
    activeFilterCount() {
      return activeCount(this.filters, TRIP_FILTER_CONFIG);
    },
    filterTooltip() {
      const parts = describeFilters(this.filters, this.filterLabel, 3, TRIP_FILTER_CONFIG);
      return parts.length ? parts.join(" · ") : null;
    },
    // Ticketarten mit eigener Kapazität (Zusatzplätze wie Fahrrad, Sperrgepäck, Rollstuhlplatz)
    extraTypes() {
      return (this.ticketTypes || [])
        .filter((tt) => tt.enable_capacity)
        .map((tt) => ({
          field: `ticket_capacity_${tt.tariff_zone_id}_${tt.ticket_type_id}`,
          usedField: `ticket_capacity_used_${tt.tariff_zone_id}_${tt.ticket_type_id}`,
          name: tt.name,
        }));
    },
    // geänderte Felder mit Werten für die Schnittstelle (Zahlen als Zahl, leer als null)
    changes() {
      const out = {};
      Object.keys(this.draft).forEach((key) => {
        if (this.draft[key] === this.original[key]) {
          return;
        }
        if (key === "state") {
          out.state = this.draft.state;
        } else {
          out[key] = this.draft[key] === "" ? null : Number(this.draft[key]);
        }
      });
      return out;
    },
    dirty() {
      return Object.keys(this.changes).length > 0;
    },
    changeText() {
      const n = Object.keys(this.changes).length;
      if (!n) {
        return "Keine Änderungen";
      }
      return n === 1 ? "1 Änderung" : n + " Änderungen";
    },
    // Zahlenfelder: ganze Zahlen ab 0 (Zusatzplätze dürfen leer sein)
    errors() {
      const out = {};
      Object.keys(this.draft).forEach((key) => {
        if (key === "state") {
          return;
        }
        const value = this.draft[key];
        if (value === "" && key.indexOf("ticket_capacity_") === 0) {
          return;
        }
        if (!/^\d+$/.test(String(value).trim())) {
          out[key] = "Bitte eine ganze Zahl ab 0 eingeben.";
        }
      });
      return out;
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
    // Auslastung der sichtbaren Fahrten: Buchungen / Kapazität (wie die frühere Summenzeile)
    utilizationText() {
      const s = this.summary.sums;
      if (!s.capacity) {
        return "–";
      }
      return formatNumber((s.used * 100) / s.capacity, 1) + " %";
    },
  },
  watch: {
    dialog: function (newDialog, oldDialog) {
      if (oldDialog == true && newDialog == false) {
        this.refreshDataGrid();
        if (this.$refs.tripDetail) {
          this.$refs.tripDetail.loadStops();
        }
      }
    },
    // offener Detailbereich: Spalten „Datum“, „Zusatzplätze“ und „Umsatz“ ausblenden (stehen dann rechts im Detail;
    // sonst wird die Tabelle zu schmal)
    selectedTrip(trip, previous) {
      if (!!trip !== !!previous) {
        const ref = this.$refs[this.gridRefName];
        if (ref && ref.instance) {
          ref.instance.beginUpdate();
          ref.instance.columnOption("extras", "visible", !trip);
          ref.instance.columnOption("price", "visible", !trip);
          ref.instance.columnOption("day", "visible", !trip);
          // Balken bei offenem Detailbereich kürzer, damit die Tabelle neben dem Detail Platz hat
          ref.instance.columnOption("capacity", "width", trip ? 150 : 220);
          ref.instance.endUpdate();
        }
      }
    },
    // anderer Monat: wie bisher alle Filter aufheben (das Datum „heute“ liegt dort meist nicht)
    month: function () {
      this.refreshDataGrid();
      this.onFiltersInput(emptyFilters(TRIP_FILTER_CONFIG));
    },
    // Kapazitäten der Ticketarten als verborgene Spalten vor „Buchungen“ (für Suche und Export)
    generatedColumns: function (newCols, oldCols) {
      if (oldCols == null) {
        const at = this.columns.findIndex((c) => c.dataField === "capacity_used");
        this.columns.splice(at === -1 ? this.columns.length : at, 0, ...newCols);
      }
    },
  },
  data() {
    return {
      month: new Date().toISOString().substr(0, 7),
      searchText: "",
      // Filter-Panel (Kundenwunsch 13.10.2026): Zustand, Werte der Listen, Treffer nach Filter und Suche
      filters: defaultTripFilters(),
      filterSegments: TRIP_FILTER_CONFIG.segments,
      resultNouns: ["Fahrt", "Fahrten"],
      facets: [],
      resultCount: null,
      summary: { count: 0, loaded: 0, sums: {} },
      dialog: false,
      emailDialog: false,
      notificationText: "Der Bus am ZOB International Bremen wartet 5 Minuten.",
      notificationSending: false,
      snackbarSuccess: false,
      snackbarError: false,
      selectedVersionId: null,
      selectedTripId: null,
      gridRefName: "shopTripDataGrid",
      generatedColumns: null,
      ticketTypes: null,
      // Detailbereich: gewählte Fahrt (eingefroren), Entwurf und gespeicherter Stand der Felder, Fehler
      selectedTrip: null,
      draft: {},
      original: {},
      saving: false,
      saveError: "",
      savedSnackbar: false,
      discardDialog: false,
      // Zeiger auf den Zeilen: ein Klick öffnet den Detailbereich (Kundenwunsch 13.10.2026)
      gridAttr: { class: "app-grid--detail" },
      // Bildschirmspalten (Kundenwünsche 13.10.2026: wenige Spalten wie Verkäufe, einzeilig und ruhig, „uhrzeit ganz nach
      // vorne und datum“, Linie und Fahrtnummer als eigene Spalten): Uhrzeit, Datum, Linie, Fahrt, Strecke, Status,
      // Auslastung, Zusatzplätze, Umsatz. Richtung, Buchungen, Rabattkarten und die Kapazitäten je Ticketart bleiben
      // verborgen für Suche und Excel-Export (onExporting); bei offenem Detailbereich entfallen Datum, Zusatzplätze und
      // Umsatz.
      columns: [
        {
          dataField: "start",
          caption: "Uhrzeit",
          width: 112,
          sortOrder: "asc",
          sortIndex: 2,
          cellTemplate: "timeCellTemplate",
          allowEditing: false,
        },
        {
          dataField: "day",
          caption: "Datum",
          width: 104,
          dataType: "date",
          sortOrder: "desc",
          sortIndex: 0,
          // Export wie bisher „13.10.2026“; auf dem Bildschirm „Di, 13.10.“ (Vorlage)
          calculateDisplayValue: function (value) {
            return value.day ? value.day_formatted : "";
          },
          cellTemplate: "dayCellTemplate",
          allowEditing: false,
        },
        {
          dataField: "line_id",
          caption: "Linie",
          width: 64,
          alignment: "left",
          allowEditing: false,
        },
        {
          dataField: "trip_id",
          caption: "Fahrt",
          width: 88,
          dataType: "number",
          alignment: "left",
          allowEditing: false,
        },
        {
          dataField: "description",
          caption: "Strecke",
          minWidth: 140,
          cellTemplate: "routeCellTemplate",
          allowEditing: false,
        },
        {
          dataField: "state",
          caption: "Status",
          width: 108,
          cellTemplate: "tripStatusTemplate",
          allowEditing: true,
          lookup: {
            dataSource: {
              store: {
                type: "array",
                data: [
                  { state: "ACTIVE", name: "Gültig" },
                  { state: "CANCELED", name: "Storniert" },
                ],
                key: "state",
              },
            },
            valueExpr: "state",
            displayExpr: "name",
          },
        },
        {
          dataField: "capacity",
          dataType: "number",
          caption: "Auslastung",
          width: 220,
          cellTemplate: "tripCapacityTemplate",
          allowEditing: true,
          calculateCellValue: function (rowData) {
            return rowData.capacity;
          },
        },
        {
          name: "extras",
          caption: "Zusatzplätze",
          width: 240,
          cellTemplate: "extrasCellTemplate",
          calculateCellValue: (row) => this.usedExtras(row).map((x) => x.name).join(" "),
          allowSorting: false,
          allowExporting: false,
        },
        {
          dataField: "capacity_used",
          caption: "Buchungen",
          visible: false,
          calculateCellValue: function (rowData) {
            return rowData.capacity_used;
          },
        },
        {
          dataField: "price",
          caption: "Umsatz",
          width: 108,
          alignment: "right",
          dataType: "number",
          format: "######0.00 €",
          // Bildschirm nach R6 („167,50 €“, unabhängig von der Browsersprache); der Export behält das Zahlenformat
          cellTemplate: "priceCellTemplate",
          allowEditing: false,
        },
        {
          dataField: "number_discount_tickets",
          caption: "Rabattkarten",
          visible: false,
          allowEditing: true,
        },
        {
          dataField: "is_outward_direction",
          caption: "Richtung",
          visible: false,
          dataType: "string",
          calculateDisplayValue: function (value) {
            return value.is_outward_direction == true ? "H" : "R";
          },
          allowEditing: false,
        },
      ],
      tripDataSource: new CustomStore({
        key: ["trip_id", "version_id"],
        load: async () => {
          const data = (
            await axios.get(
              `${this.$store.state.baseUrl}shoptrip?month=${moment(
                this.month + "-01"
              ).format("MM")}&year=${moment(this.month + "-01").format("YYYY")}`
            )
          ).data;
          this.generateColumns(data);
          this._rows = Array.isArray(data) ? data : [];
          this.refreshFacets();
          this.syncSelectedTrip();
          return data;
        },
        update: (tripKeys, tripUpdates) =>
          axios.put(
            `${this.$store.state.baseUrl}shoptrip?trip_id=${tripKeys.trip_id}&version_id=${tripKeys.version_id}`,
            tripUpdates
          ),
      }),
    };
  },
  created() {
    // nicht reaktiv: Fortsetzung nach „Verwerfen“
    this.pendingDiscard = null;
  },
  mounted() {
    // Vorbelegung „heute“ wie bisher der Datumsfilter der Spalte
    this.applyFilters();
  },
  methods: {
    // ---------------------------------------------------------------- Filter-Panel (Kundenwunsch 13.10.2026)
    emptyTripFilters() {
      return defaultTripFilters();
    },
    filterLabel(listKey, key) {
      if (key === "") {
        return NO_VALUE_TEXT;
      }
      if (listKey === "days") {
        const m = moment(key, "YYYY-MM-DD", true);
        // Datum vorn, damit die Liste nach Tagen sortiert (sie sortiert nach dem Text); Wochentag dahinter
        return m.isValid() ? m.format("DD.MM.YYYY (dd)") : key;
      }
      return key;
    },
    refreshFacets() {
      this.facets = Object.freeze(buildFacets(this._rows || [], this.filters, this.filterLabel, TRIP_FILTER_CONFIG));
    },
    // Filter als Prüffunktion auf der Datenquelle wie bei Verkäufen (jedes Mal eine neue Funktion)
    onFiltersInput(filters) {
      this.filters = filters || this.emptyTripFilters();
      this.applyFilters();
      this.refreshFacets();
    },
    applyFilters() {
      const grid = this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance;
      if (grid) {
        const match = buildPredicate(this.filters, TRIP_FILTER_CONFIG);
        grid.filter(match ? [match, "=", true] : null);
      }
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Math.max(Number(grid.totalCount()) || 0, 0);
      this.resultCount = count;
      const rows = this._rows || [];
      let visible = rows;
      const filter = grid.getCombinedFilter();
      if (filter && rows.length) {
        try {
          visible = query(rows).filter(filter).toArray();
        } catch (err) {
          visible = rows;
        }
      }
      const sums = {};
      sums.price = visible.reduce((sum, row) => sum + (Number(row.price) || 0), 0);
      sums.used = visible.reduce((sum, row) => sum + (Number(row.capacity_used) || 0), 0);
      sums.capacity = visible.reduce((sum, row) => sum + (Number(row.capacity) || 0), 0);
      this.summary = { count, loaded: Math.max(rows.length, count), sums };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    // nach dem Neuladen den Stand der gewählten Fahrt übernehmen (Entwurf bleibt, wenn ungespeichert)
    syncSelectedTrip() {
      if (!this.selectedTrip) {
        return;
      }
      const fresh = (this._rows || []).find((row) => this.sameTrip(row, this.selectedTrip));
      if (!fresh) {
        return;
      }
      this.selectedTrip = Object.freeze(Object.assign({}, fresh));
      if (!this.dirty) {
        this.original = this.draftFrom(this.selectedTrip);
        this.draft = Object.assign({}, this.original);
      }
    },
    sumText(key) {
      return formatEuro(this.summary.sums[key] || 0);
    },
    onSearchInput() {
      this.$refs[this.gridRefName].instance.searchByText(this.searchText || "");
    },
    onToolbarPreparing(e) {
      // Remove all default toolbar items; die leere Leiste ganz ausblenden (sonst zweite Linie über dem Tabellenkopf)
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
    // ---------------------------------------------------------------- Bildschirmspalten
    routeText(row) {
      return String(row.description || "–").replace(/\s*->\s*/g, " → ");
    },
    euroText(value) {
      return value === null || value === undefined || value === "" ? "–" : formatEuro(Number(value));
    },
    dayText(row) {
      const m = row.day ? moment(row.day).locale("de") : null;
      return m && m.isValid() ? m.format("dd, DD.MM.") : row.day_formatted || "";
    },
    // belegte Zusatzplätze einer Zeile [{ field, name, used, capacity }]
    usedExtras(row) {
      return this.extraTypes
        .map((t) => ({ field: t.field, name: t.name, used: Number(row[t.usedField]) || 0, capacity: row[t.field] }))
        .filter((x) => x.used > 0);
    },
    // ---------------------------------------------------------------- Detailbereich: Auswahl und Entwurf
    draftFrom(trip) {
      const text = (v) => (v === null || v === undefined ? "" : String(v));
      const draft = {
        state: trip.state,
        capacity: text(trip.capacity),
        number_discount_tickets: text(trip.number_discount_tickets),
      };
      this.extraTypes.forEach((t) => {
        draft[t.field] = text(trip[t.field]);
      });
      return draft;
    },
    selectTrip(row) {
      this.selectedTrip = Object.freeze(Object.assign({}, row));
      this.original = this.draftFrom(this.selectedTrip);
      this.draft = Object.assign({}, this.original);
      this.saveError = "";
    },
    onField(key, value) {
      this.draft = Object.assign({}, this.draft, { [key]: value === undefined || value === null ? "" : String(value) });
      this.saveError = "";
    },
    discardDraft() {
      this.draft = Object.assign({}, this.original);
      this.saveError = "";
    },
    onFocusedRowChanging(e) {
      if (this.saving) {
        e.cancel = true;
        return;
      }
      if (!this.dirty || e.newRowIndex === e.prevRowIndex) {
        return;
      }
      e.cancel = true;
      const index = e.newRowIndex;
      this.askDiscard(() => {
        const grid = this.$refs[this.gridRefName].instance;
        this.discardDraft();
        grid.option("focusedRowIndex", index);
      });
    },
    onFocusedRowChanged(e) {
      const row = e.row;
      if (!row || row.rowType !== "data" || !row.data) {
        return;
      }
      if (this.selectedTrip && this.sameTrip(row.data, this.selectedTrip)) {
        return;
      }
      this.selectTrip(row.data);
    },
    sameTrip(a, b) {
      return a && b && a.trip_id === b.trip_id && a.version_id === b.version_id;
    },
    onDetailClose() {
      if (this.dirty) {
        this.askDiscard(() => this.closeDetail());
        return;
      }
      this.closeDetail();
    },
    closeDetail() {
      this.selectedTrip = null;
      this.draft = {};
      this.original = {};
      this.saveError = "";
      const grid = this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance;
      if (grid && grid.option("focusedRowIndex") !== -1) {
        grid.option("focusedRowIndex", -1);
      }
    },
    onDetailLayout() {
      this.$nextTick(() => {
        const ref = this.$refs[this.gridRefName];
        if (ref && ref.instance) {
          ref.instance.updateDimensions();
        }
      });
    },
    askDiscard(onDiscard) {
      this.pendingDiscard = onDiscard;
      this.discardDialog = true;
    },
    onDiscardDialogInput(open) {
      if (!open) {
        this.keepEditing();
      }
    },
    keepEditing() {
      this.discardDialog = false;
      this.pendingDiscard = null;
    },
    confirmDiscard() {
      const next = this.pendingDiscard;
      this.discardDialog = false;
      this.pendingDiscard = null;
      if (next) {
        next();
      }
    },
    // geänderte Felder speichern (dieselbe Schnittstelle wie bisher das Bearbeiten in der Tabelle: PUT shoptrip)
    async saveTrip() {
      if (!this.dirty || this.hasErrors || this.saving) {
        return;
      }
      const trip = this.selectedTrip;
      this.saving = true;
      this.saveError = "";
      try {
        await this.tripDataSource.update({ trip_id: trip.trip_id, version_id: trip.version_id }, this.changes);
        this.original = Object.assign({}, this.draft);
        this.savedSnackbar = true;
        this.refreshDataGrid();
      } catch (err) {
        this.saveError = "Die Fahrt konnte nicht gespeichert werden.";
      } finally {
        this.saving = false;
      }
    },
    editTimes() {
      if (!this.selectedTrip) {
        return;
      }
      this.selectedVersionId = this.selectedTrip.version_id;
      this.selectedTripId = this.selectedTrip.trip_id;
      this.dialog = true;
    },
    async generateColumns(data) {
      if (!data || data.length == 0) return;
      try {
        this.ticketTypes = (
          await axios.get(`${this.$store.state.baseUrl}tickettype?all=1`)
        ).data;
        const cols = this.ticketTypes
          .filter((tt) => tt.enable_capacity)
          .map((tt) => {
            const col = `ticket_capacity_${tt.tariff_zone_id}_${tt.ticket_type_id}`;
            const obj = {
              dataField: col,
              caption: tt.name,
              visible: false,
              allowEditing: true,
              calculateDisplayValue: function (value) {
                return value[col]
                  ? `${value[col.replace("ticket_capacity_", "ticket_capacity_used_")]}/${
                      value[col]
                    }`
                  : null;
              },
            };
            return obj;
          });
        this.generatedColumns = cols;
      } catch (e) {
        console.log(e);
      }
    },
    refreshDataGrid() {
      this.$refs[this.gridRefName].instance.refresh();
    },
    // Farbe der Auslastung wie früher (Erfolg, Warnung, Fehler) mit der Ampel des Dashboards
    capacityColor(data) {
      const level = this.getCapacityColor(data);
      return level === "error" ? "st-late" : level === "warning" ? "st-warn" : "st-ok";
    },
    getCapacityColor(data) {
      const usedPercentage = (data.capacity_used * 100) / data.capacity;
      if (usedPercentage >= 80) {
        return "error";
      } else if (usedPercentage >= 50) {
        return "warning";
      } else {
        return "success";
      }
    },
    openNotification() {
      if (!this.selectedTrip) {
        return;
      }
      this.selectedVersionId = this.selectedTrip.version_id;
      this.selectedTripId = this.selectedTrip.trip_id;
      this.emailDialog = true;
    },
    async sendNotification() {
      try {
        this.notificationSending = true;
        this.snackbarSuccess = false;
        this.snackbarError = false;
        await axios.post(`${this.$store.state.baseUrl}shoptripnotification`, {
          text: this.notificationText,
          trip_id: this.selectedTripId,
        });
        this.snackbarSuccess = true;
        this.emailDialog = false;
      } catch (e) {
        this.snackbarError = true;
      } finally {
        this.notificationSending = false;
      }
    },
    calculateSelectedRow(options) {
      if (options.name === "SelectedRowsSummary") {
        if (options.summaryProcess === "start") {
          options.totalValue = {
            capacity_used: 0,
            capacity: 0,
          };
        } else if (options.summaryProcess === "calculate") {
          options.totalValue.capacity_used =
            options.totalValue.capacity_used + Number(options.value.capacity_used);
          options.totalValue.capacity =
            options.totalValue.capacity + options.value.capacity;
        } else if (options.summaryProcess === "finalize") {
          options.totalValue =
            (options.totalValue.capacity_used / options.totalValue.capacity) * 100;
        }
      }
    },
    onExporting() {
      const component = this.$refs[this.gridRefName].instance;
      // Export wie bisher mit allen Datenspalten: Bildschirmspalten aus, Datenspalten an (danach zurück)
      const screen = ["extras", "state", "number_discount_tickets"];
      const extrasShown = !this.selectedTrip;
      const data = ["day", "is_outward_direction", "capacity_used", "price"].concat(this.extraTypes.map((t) => t.field));
      component.beginUpdate();
      screen.forEach((c) => component.columnOption(c, "visible", false));
      data.forEach((c) => component.columnOption(c, "visible", true));

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Preise");
      exportDataGrid({
        component: component,
        worksheet: worksheet,
        customizeCell: function (options) {
          const excelCell = options;
          excelCell.font = { name: "Arial", size: 12 };
          excelCell.alignment = { horizontal: "left" };
        },
      }).then(function () {
        workbook.xlsx
          .writeBuffer()
          .then(function (buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              `Fahrten.xlsx`
            );
          })
          .then(function () {
            data.forEach((c) => component.columnOption(c, "visible", false));
            component.columnOption("state", "visible", true);
            component.columnOption("extras", "visible", extrasShown);
            component.columnOption("price", "visible", extrasShown);
            component.columnOption("day", "visible", extrasShown);
            component.endUpdate();
          });
      });
      // e.cancel = true;
    },
  },
};
</script>

<style scoped>
/* Tabelle und Fußleiste als Spalte links vom Detailbereich (wie Verkäufe) */
.trip-gridcol {
  flex: 999 1 560px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
/* Reihenfolge der Sortierung (Datum, dann Uhrzeit) ohne Ziffern im Kopf; die Pfeile bleiben */
.trip-gridcol ::v-deep .dx-sort-index-icon {
  display: none;
}
/* Bildschirmspalten einzeilig: Uhrzeit hervorgehoben, Strecke mit Ellipse */
.trip-gridcol ::v-deep .trip-time {
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.trip-gridcol ::v-deep .trip-day,
.trip-gridcol ::v-deep .trip-price {
  font-variant-numeric: tabular-nums;
}
.trip-gridcol ::v-deep .trip-route {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Zusatzplätze als Chips */
.trip-gridcol ::v-deep .trip-extras {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.trip-gridcol ::v-deep .trip-extras__chip {
  padding: 1px 6px;
  border-radius: 9999px;
  background: var(--tint);
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-2);
  white-space: nowrap;
}
.trip-gridcol ::v-deep .trip-extras__chip strong {
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.trip-gridcol ::v-deep .trip-extras__none {
  font-size: 12px;
  color: var(--ink-3);
}
.trip-detail-tag {
  font-size: 12px;
  font-weight: 600;
  color: var(--red);
}
/* Fuß des Detailbereichs wie bei Benutzer */
.trip-foot__text {
  flex: 1 1 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: -4px -16px 0;
  padding: 8px 16px;
  border-bottom: 1px solid var(--line-2);
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-2);
}
.trip-foot__text--error {
  color: var(--red);
}
p.trip-dialog__text {
  margin: 0;
}
/* Handy: Grid in natürlicher Höhe */
.trip-grid.trip-grid--auto {
  flex: none;
}
/* Summenzeile nur für den Export; auf dem Bildschirm stehen Umsatz und Auslastung in der Fußleiste */
.trip-page ::v-deep .dx-datagrid-total-footer {
  display: none;
}
.trip-capacity {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 112px;
}
/* Balken wie im Dashboard (AppShareBar: 6 hoch, Radius 2, Spur --d-trk) */
.trip-capacity__bar {
  flex: 1 1 auto;
  min-width: 48px;
}
.trip-capacity__text {
  flex: none;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}
.trip-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.trip-status__dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
.trip-status__dot--ok {
  background: var(--green-dot);
}
.trip-status__dot--off {
  background: var(--red);
}
</style>
